import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import MoveComposer from "../components/MoveComposer";

const isSmall = () => window.matchMedia("(max-width: 720px)").matches;

function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  if (el.isContentEditable) return true;
  return !!el.closest && !!el.closest("input,textarea,[contenteditable='true']");
}

function orthogonalPathFan(a, b, fanY) {
  // Route via a short horizontal "bus" at fanY to avoid ambiguous crossings
  return `M ${a.x},${a.y} L ${a.x},${fanY} L ${b.x},${fanY} L ${b.x},${b.y}`;
}

function Node({ move, activeId, setActiveId, onVote, onMeasure }) {
  const classes = ["hm-node", `t-${move.type}`];
  if (move.moderation === "blurred") classes.push("hm-blurred");
  const elRef = useRef(null);
  useEffect(() => {
    const h = elRef.current?.offsetHeight || 0;
    if (h) onMeasure?.(move.id, h);
  }, [move.id, move.text, move.link, move.moderation, onMeasure]);
  return (
    <div
      ref={elRef}
      className={classes.join(" ")}
      style={{ left: move.__x, top: move.__y }}
      onMouseEnter={() => setActiveId(move.id)}
      onMouseLeave={() => setActiveId(null)}
      onClick={() => setActiveId(activeId === move.id ? null : move.id)}
    >
      <div className="hm-card-head">
        <span className={`chip t-${move.type}`}>{String(move.type).toUpperCase()}</span>
        <span className="hm-meta">{move.username}</span>
      </div>
      <div className="hm-card-body">
        {move.moderation === "removed" ? (
          <em className="removed">[Removed by community]</em>
        ) : (
          <p className="hm-text">{move.text}</p>
        )}
        {move.link && (
          <a className="hm-link" href={move.link} target="_blank" rel="noreferrer">
            {move.link}
          </a>
        )}
      </div>
      <div className={`hm-vote-bar ${activeId === move.id ? "show" : ""}`}>
        <button onClick={() => onVote(move.id, "like")}>👍 {move.like_count}</button>
        <button onClick={() => onVote(move.id, "useful")}>💡 {move.useful_count}</button>
        <button onClick={() => onVote(move.id, "dislike")}>👎 {move.dislike_count}</button>
        <button onClick={() => onVote(move.id, "report")}>⚠️ {move.report_count}</button>
      </div>
      {move.moderation === "blurred" && <div className="hm-blur-label">Moderated content · See anyway</div>}
    </div>
  );
}

function StackedCards({ map, moves, onVote, onPost }) {
  const [showVotesId, setShowVotesId] = useState(null);
  const issue = moves.find((m) => m.type === "issue");
  const positions = moves.filter((m) => m.type === "position");

  const posById = Object.fromEntries(positions.map((p) => [p.id, p]));
  const grouped = positions.map((p) => ({
    position: p,
    parent: p.parent_id ? posById[p.parent_id] || issue : issue,
    for: moves.filter((m) => m.parent_id === p.id && m.type === "for"),
    against: moves.filter((m) => m.parent_id === p.id && m.type === "against"),
    risk: moves.filter((m) => m.parent_id === p.id && m.type === "risk"),
    evidence: moves.filter((m) => m.parent_id === p.id && m.type === "evidence"),
    childrenPositions: positions.filter((m) => m.parent_id === p.id),
  }));

  const Arg = ({ a }) => (
    <li className="arg-row" onClick={() => setShowVotesId((s) => (s === a.id ? null : a.id))}>
      <div className="txt">{a.text}</div>
      {a.link && (
        <a href={a.link} target="_blank" rel="noreferrer" className="lnk">
          {a.link}
        </a>
      )}
      {showVotesId === a.id && (
        <div className="votes">
          <button onClick={(e) => { e.stopPropagation(); onVote(a.id, "like"); }}>👍 {a.like_count}</button>
          <button onClick={(e) => { e.stopPropagation(); onVote(a.id, "useful"); }}>💡 {a.useful_count}</button>
          <button onClick={(e) => { e.stopPropagation(); onVote(a.id, "dislike"); }}>👎 {a.dislike_count}</button>
          <button onClick={(e) => { e.stopPropagation(); onVote(a.id, "report"); }}>⚠️ {a.report_count}</button>
        </div>
      )}
    </li>
  );

  const Group = ({ label, items }) =>
    items.length ? (
      <div className="group">
        <div className={`group-pill t-${label}`}>{label.toUpperCase()}</div>
        <ul className="group-list">{items.map((a) => <Arg key={a.id} a={a} />)}</ul>
      </div>
    ) : null;

  return (
    <>
      <div className="mobile-stack">
        <div className="mobile-issue">
          <div className="chip t-issue">Issue</div>
          <h3>{issue?.text || map.title}</h3>
        </div>

        {grouped.map((g) => (
          <section key={g.position.id} className="mobile-section">
            <header className="mobile-pos-head">
              <div className="chip t-position">Position</div>
              <h4>{g.position.text}</h4>
              <div className="parent-crumb">
                ↳ {g.parent?.type === "issue" ? "Issue" : `Position: ${g.parent?.text || ""}`}
              </div>
              <div className="seg-tabs">
                <span className="seg t-for">For ({g.for.length})</span>
                <span className="seg t-against">Against ({g.against.length})</span>
                <span className="seg t-risk">Risk ({g.risk.length})</span>
                <span className="seg t-evidence">Evidence ({g.evidence.length})</span>
                {g.childrenPositions.length ? <span className="seg t-position">Positions ({g.childrenPositions.length})</span> : null}
              </div>
            </header>
            <Group label="for" items={g.for} />
            <Group label="against" items={g.against} />
            <Group label="risk" items={g.risk} />
            <Group label="evidence" items={g.evidence} />
          </section>
        ))}
      </div>

      <MoveComposer mapId={map.id} moves={moves} onPost={onPost} />
    </>
  );
}

export default function MapView({ map, moves, onVote, onPost }) {
  const [activeId, setActiveId] = useState(null);
  const [sizes, setSizes] = useState({});
  const onMeasure = useCallback((id, h) => {
    // de-noise tiny changes so we don’t thrash layout
    setSizes((prev) => (h && Math.abs((prev[id] || 0) - h) > 2 ? { ...prev, [id]: h } : prev));
  }, []);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spacePan, setSpacePan] = useState(false);
  const [mobile, setMobile] = useState(isSmall());
  const wrapRef = useRef(null);
  const pointerState = useRef({ dragging: false, start: null, startPan: null, id: null });
  const stageSizeRef = useRef({ w: 2200, h: 1400 });

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 720px)");
    const handler = (e) => setMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Spacebar pan (skip when typing)
  useEffect(() => {
    function keydown(e) {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        setSpacePan(true);
        e.preventDefault();
      }
    }
    function keyup(e) {
      if (e.code === "Space") setSpacePan(false);
    }
    window.addEventListener("keydown", keydown, { passive: false });
    window.addEventListener("keyup", keyup);
    return () => {
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("keyup", keyup);
    };
  }, []);

  // Layout + connectors + dynamic stage size
  const { layout, edges, stageW, stageH } = useMemo(() => {
    const out = { layout: [], edges: [], stageW: 2200, stageH: 1400 };
    if (!moves?.length) return out;

    // --- constants ---
    const W = 260, BASE_H = 180, GAPX = 24, GAPY = 56;
    const originX = 640, originY = 60;
    const PAD = 160;
    // Slightly wider separation + slightly narrower lane to avoid cross-lane collisions
    const POS_SPACING = 2 * (W + GAPX) + 40;
    const LANE_HALF = W + GAPX;
    // use measured height when we have it; clamp to a sane max so one essay doesn't blow up spacing
    const getH = (m) => Math.max(BASE_H, Math.min(sizes[m.id] || BASE_H, 520));

    // lookup helpers
    const issue = moves.find((m) => m.type === "issue");
    const positions = moves.filter((m) => m.type === "position");
    const itemsByParentType = (pid, type) => moves.filter((m) => m.parent_id === pid && m.type === type);
    const childPositionsOf = (pid) => positions.filter((p) => p.parent_id === pid);

    // placement helpers
    const placed = [];
    const place = (m, x, y) => {
      m.__x = x;
      m.__y = y;
      m.__h = getH(m);
      placed.push(m);
    };

    function addEdgeFan(fromNode, toNode, rowIndex) {
      const a = { x: fromNode.__x + W / 2, y: fromNode.__y + (fromNode.__h || BASE_H) };
      const b = { x: toNode.__x + W / 2, y: toNode.__y };
      const fanY = a.y + 24 + (rowIndex || 0) * 10; // small per-row separation
      out.edges.push({ a, b, fanY });
    }

    // --- positions under issue ---
    if (issue) {
      place(issue, originX, originY);
      const tops = positions.filter((p) => p.parent_id === issue.id);
      const posY = originY + (issue.__h || BASE_H) + 60;
      let firstX = originX - ((tops.length - 1) * POS_SPACING) / 2;
      tops.forEach((p, i) => {
        const px = firstX + i * POS_SPACING;
        place(p, px, posY);
        addEdgeFan(issue, p, 0);
      });
    }

    // --- recursive placement of each position's children within its lane ---
    function placePositionChildren(p) {
      const laneLeft = p.__x - LANE_HALF;
      const laneRight = p.__x + LANE_HALF;

      const rows = ["for", "against", "risk", "evidence"];
      const rowOffset = {
        for: - (W + GAPX) / 2,
        against: (W + GAPX) / 2,
        risk: - (W + GAPX) / 4,
        evidence: (W + GAPX) / 4
      };

      // Start just under the position card, then for EACH row:
      // place children, remember the tallest card in that row, and bump y by that height + GAPY.
      let yCursor = p.__y + (p.__h || BASE_H) + 40;
      rows.forEach((row, ridx) => {
        const arr = itemsByParentType(p.id, row);
        let tallest = BASE_H;
        arr.forEach((m, j) => {
          const thisH = getH(m);
          tallest = Math.max(tallest, thisH);

          // base left/right grouping but clamped within the lane
          const baseX = p.__x + (rowOffset[row] || 0) + j * (W + GAPX);
          let x = Math.max(laneLeft, Math.min(laneRight - W, baseX));
          x += (row === "for" ? -1 : row === "against" ? 1 : 0) * 8; // tiny diverge to reduce ambiguity

          m.__x = x;
          m.__y = yCursor;
          m.__h = thisH;
          out.layout.push(m);
          addEdgeFan(p, m, ridx);
        });
        if (arr.length) yCursor += tallest + GAPY;
      });

      // chain child positions diagonally within/near their parent's lane
      const kids = childPositionsOf(p.id);
      kids.forEach((cp, idx) => {
        const childX = Math.min(laneRight - W, Math.max(laneLeft, p.__x + (idx + 1) * (W + GAPX) * 0.8));
        const childY = yCursor + 20;
        place(cp, childX, childY);
        addEdgeFan(p, cp, 0);
        placePositionChildren(cp);
      });
    }

    const allPositions = positions.sort((a, b) => a.__y - b.__y || a.__x - b.__x);
    allPositions.forEach((p) => { if (p.__x != null) placePositionChildren(p); });

    out.layout.push(...placed);

    // --- collision pass: push later nodes down if they intersect earlier ones ---
    out.layout.sort((a, b) => a.__y - b.__y || a.__x - b.__x);
    const placedRects = [];
    for (const m of out.layout) {
      let my = m.__y;
      const mh = m.__h || BASE_H;
      const mw = W;

      // check against all previously accepted rects
      for (const r of placedRects) {
        const overlapX = !(m.__x + mw <= r.x || r.x + r.w <= m.__x);
        const overlapY = !(my + mh <= r.y || r.y + r.h <= my);
        if (overlapX && overlapY) {
          // push this card just below the conflicting one
          my = r.y + r.h + GAPY;
        }
      }

      m.__y = my;
      placedRects.push({ x: m.__x, y: my, w: mw, h: mh });
    }

    // rebuild edges after collision adjustments
    out.edges = [];
    const byId = Object.fromEntries(out.layout.map(n => [n.id, n]));
    for (const m of out.layout) {
      const parent = byId[m.parent_id];
      if (!parent) continue;
      const a = { x: parent.__x + W / 2, y: parent.__y + (parent.__h || BASE_H) };
      const b = { x: m.__x + W / 2, y: m.__y };
      const fanY = a.y + 24; // small horizontal bus to reduce crossovers
      out.edges.push({ a, b, fanY });
    }

    // --- compute extents ---
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    out.layout.forEach((m) => {
      minX = Math.min(minX, m.__x);
      minY = Math.min(minY, m.__y);
      maxX = Math.max(maxX, m.__x + W);
      maxY = Math.max(maxY, m.__y + (m.__h || BASE_H));
    });
    if (isFinite(minX)) {
      out.stageW = Math.max(1200, Math.ceil(maxX - minX + PAD));
      out.stageH = Math.max(800, Math.ceil(maxY - minY + PAD));
    }

    return out;
  }, [moves, sizes]);

  // keep stage dimensions for pan clamping
  useEffect(() => { stageSizeRef.current = { w: stageW, h: stageH }; }, [stageW, stageH]);

  // Pointer + wheel pan
  useEffect(() => {
    if (mobile) return;
    const el = wrapRef.current;
    if (!el) return;

    function clamp(nx, ny) {
      const wrap = el.getBoundingClientRect();
      const { w, h } = stageSizeRef.current;
      const margin = 120;
      const minX = Math.min(0, wrap.width - w) - margin;
      const maxX = margin;
      const minY = Math.min(0, wrap.height - h) - margin;
      const maxY = margin;
      const x = Math.max(minX, Math.min(maxX, nx));
      const y = Math.max(minY, Math.min(maxY, ny));
      return { x, y };
    }

    function onPointerDown(e) {
      const onNode = !!e.target.closest(".hm-node");
      const canPan = spacePan || e.button === 1 || !onNode;
      if (!canPan) return;
      pointerState.current = {
        dragging: true,
        id: e.pointerId,
        start: { x: e.clientX, y: e.clientY },
        startPan: { ...pan },
      };
      el.setPointerCapture?.(e.pointerId);
      el.classList.add("grabbing");
      e.preventDefault();
    }

    function onPointerMove(e) {
      const ps = pointerState.current;
      if (!ps.dragging || (ps.id != null && ps.id !== e.pointerId)) return;
      const dx = e.clientX - ps.start.x;
      const dy = e.clientY - ps.start.y;
      setPan((prev) => clamp(ps.startPan.x + dx, ps.startPan.y + dy));
    }

    function endDrag(e) {
      const ps = pointerState.current;
      if (ps.dragging && (ps.id === e.pointerId || e.type === "pointercancel" || e.type === "pointerup")) {
        pointerState.current.dragging = false;
        el.classList.remove("grabbing");
      }
    }

    function onWheel(e) { setPan((prev) => clamp(prev.x - e.deltaX, prev.y - e.deltaY)); }

    el.addEventListener("pointerdown", onPointerDown, { passive: false });
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("wheel", onWheel);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("wheel", onWheel);
    };
  }, [mobile, pan, spacePan]);

  if (mobile) {
    return <StackedCards map={map} moves={moves} onVote={onVote} onPost={onPost} />;
  }

  return (
    <>
      <div className="hm-map-wrap" ref={wrapRef} title="Drag to pan. Hold Space to pan from any point.">
        <svg className="hm-stage svg" width={stageW} height={stageH} style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}>
          <defs>
            <marker id="hm-arrow" markerWidth="12" markerHeight="8" refX="8" refY="4" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 12,4 0,8" />
            </marker>
          </defs>
          {edges.map((e, i) => (
            <path key={i} className="edge" d={orthogonalPathFan(e.a, e.b, e.fanY)} markerEnd="url(#hm-arrow)" />
          ))}
        </svg>
        <div className="hm-stage nodes" style={{ width: stageW, height: stageH, transform: `translate(${pan.x}px, ${pan.y}px)` }}>
          {layout.map((m) => (
            <Node
              key={m.id}
              move={m}
              activeId={activeId}
              setActiveId={setActiveId}
              onVote={onVote}
              onMeasure={onMeasure}
            />
          ))}
        </div>
      </div>
      <MoveComposer mapId={map.id} moves={moves} onPost={onPost} />
    </>
  );
}
